import { useEffect, useState } from 'react';
import { collection, getCountFromServer } from 'firebase/firestore';
import { firestore } from '@/lib/firebase';

export type TenantDeletableCounts = {
  products: number;
  labels: number;
  payments: number;
};

export type TenantDeletableState =
  | { kind: 'LOADING' }
  | { kind: 'DELETABLE' }
  | { kind: 'BLOCKED'; counts: TenantDeletableCounts }
  | { kind: 'ERROR' };

/**
 * Conta produtos/etiquetas/pagamentos de um tenant (uma vez, ao montar) e
 * decide se ele pode ser excluído definitivamente.
 *
 * Origem: sem issue — commit a62adb1e3, 2026-06-11 22:55 +0200 (exclusão
 * definitiva de tenant vazio em aba dedicada).
 *
 * Por quê: nasceu para mostrar a aba "Excluir" só quando o tenant está vazio.
 * Desde o purge forçado (#83, 2026-06-22) a aba aparece em todo estado que
 * não seja `LOADING` (vazio confirma pelo nome; com dados ou contagem falha
 * exige `force`), e o resultado daqui só adia a aba até a contagem chegar. Um
 * tenant só é `DELETABLE` quando NÃO tem nenhum pagamento, produto nem
 * etiqueta — o mesmo critério do callable `hardDeleteEmptyTenant`
 * (`apps/functions/src/admin/hardDeleteEmptyTenant.ts`), que é o guard
 * autoritativo. Aqui é só afordância: o servidor reconta antes de apagar.
 *
 * Contrato: lê uma vez por `tenantId` (sem listener — a contagem não muda
 * enquanto o operador olha a aba). `LOADING` → `DELETABLE` | `BLOCKED`
 * (com as contagens) | `ERROR`. Erro de leitura não libera nada: o diálogo
 * faz a própria contagem e, se ela também falhar, exige o caminho forçado.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (monta a aba `TenantDeleteTab`
 * quando o estado sai de `LOADING`).
 *
 * Dados: três `getCountFromServer` — agregação, não baixa documentos (custo
 * de 1 leitura por 1000 docs) — em `tenants/{tenantId}/products`,
 * `tenants/{tenantId}/labels` e `tenants/{tenantId}/payments`. Exige que a
 * rule de `list` dessas subcoleções aceite super-admin.
 *
 * Falhas conhecidas: o `catch` só faz `console.error` — sem report para a
 * telemetria (CLAUDE.md "Observabilidade"); falha aqui é invisível em
 * produção (ver #926, achados).
 *
 * Issues: #83 (purge forçado), #789 (cobrança viva bloqueia a exclusão).
 */
export function useTenantDeletable(tenantId: string): TenantDeletableState {
  const [state, setState] = useState<TenantDeletableState>({ kind: 'LOADING' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'LOADING' });
    // Subcoleção do tenant pelo nome — só para encurtar as três contagens.
    const sub = (name: string): ReturnType<typeof collection> =>
      collection(firestore, 'tenants', tenantId, name);
    Promise.all([
      getCountFromServer(sub('products')),
      getCountFromServer(sub('labels')),
      getCountFromServer(sub('payments')),
    ])
      .then(([p, l, pay]): void => {
        if (cancelled) return;
        const counts: TenantDeletableCounts = {
          products: p.data().count,
          labels: l.data().count,
          payments: pay.data().count,
        };
        const empty =
          counts.products === 0 && counts.labels === 0 && counts.payments === 0;
        setState(empty ? { kind: 'DELETABLE' } : { kind: 'BLOCKED', counts });
      })
      .catch((err: unknown): void => {
        if (cancelled) return;
        // Server revalida no callable — afordância degrada para "esconde a aba".
        console.error('admin/useTenantDeletable failed', err);
        setState({ kind: 'ERROR' });
      });
    return (): void => {
      cancelled = true;
    };
  }, [tenantId]);

  return state;
}
